import React from 'react'
import { cn } from '@/utils/cn'
import { formatCurrency } from '@/utils/formatters'
import { WalletIcon, ArrowDownIcon, ArrowUpIcon } from '@heroicons/react/24/outline'

interface UserBalanceCardProps {
    balance: number
    pending: number
    earned: number
    currency?: string
    onWithdraw?: () => void
    withdrawalDisabled?: boolean
    loading?: boolean
    className?: string
}

export const UserBalanceCard: React.FC<UserBalanceCardProps> = ({
    balance,
    pending,
    earned,
    currency = 'GHS',
    onWithdraw,
    withdrawalDisabled = false,
    loading = false,
    className
}) => {
    return (
        <div className={cn('bg-gradient-to-br from-primary-500 to-primary-700 dark:from-primary-700 dark:to-primary-900 rounded-lg text-white overflow-hidden', className)}>
            <div className="p-6 sm:p-8">
                {/* Header */}
                <div className="flex items-center justify-between mb-8">
                    <div className="flex items-center gap-3">
                        <div className="bg-white/20 p-3 rounded-lg">
                            <WalletIcon className="w-6 h-6" />
                        </div>
                        <div>
                            <p className="text-sm text-white/80">Available Balance</p>
                            <h2 className="text-2xl sm:text-3xl font-bold text-white">
                                {formatCurrency(balance, currency)}
                            </h2>
                        </div>
                    </div>
                    {onWithdraw && (
                        <button
                            onClick={onWithdraw}
                            disabled={withdrawalDisabled || loading || balance <= 0}
                            className={cn(
                                'px-4 py-2 bg-white text-primary-700 font-medium rounded-lg transition-colors whitespace-nowrap text-sm',
                                withdrawalDisabled || loading || balance <= 0
                                    ? 'opacity-50 cursor-not-allowed'
                                    : 'hover:bg-white/90'
                            )}
                        >
                            {loading ? 'Processing...' : 'Withdraw'}
                        </button>
                    )}
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-2 gap-4">
                    {/* Pending */}
                    <div className="bg-white/10 rounded-lg p-4 border border-white/20">
                        <div className="flex items-center gap-2 mb-2">
                            <ArrowDownIcon className="w-4 h-4 text-yellow-300" />
                            <p className="text-sm text-white/80">Pending</p>
                        </div>
                        <p className="text-xl sm:text-2xl font-bold text-white">
                            {formatCurrency(pending, currency)}
                        </p>
                    </div>

                    {/* Total Earned */}
                    <div className="bg-white/10 rounded-lg p-4 border border-white/20">
                        <div className="flex items-center gap-2 mb-2">
                            <ArrowUpIcon className="w-4 h-4 text-green-300" />
                            <p className="text-sm text-white/80">Total Earned</p>
                        </div>
                        <p className="text-xl sm:text-2xl font-bold text-white">
                            {formatCurrency(earned, currency)}
                        </p>
                    </div>
                </div>

                {/* Info */}
                <p className="text-xs text-white/60 mt-6">
                    💡 Withdrawals are processed every Friday to your registered payment method.
                </p>
            </div>
        </div>
    )
}